import React, { useEffect, useState } from "react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { parseDateToTimestamp } from "../../../utils/date";
import { storage } from "../../../firebase";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  Field,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { useParent } from "../../../contexts/ParentContext";
import { useAccount } from "../../../contexts/AccountContext";
import {
  Address,
  CustodyStatus,
  Parent,
  RelationshipToChild,
  storagePaths,
} from "@visitproof/types";
import { Textarea } from "../../../design/textarea";
import { Select } from "../../../design/select";
import { Switch, SwitchField } from "../../../design/switch";
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import {
  Race,
  Sex,
  PrimaryLanguage,
  MaritalStatus,
  IncomeLevel,
  EducationLevel,
  EmploymentStatus,
  HousingStatus,
  RACE_LABELS,
  SEX_LABELS,
  LANGUAGE_LABELS,
  MARITAL_STATUS_LABELS,
  INCOME_LEVEL_LABELS,
  EDUCATION_LEVEL_LABELS,
  EMPLOYMENT_STATUS_LABELS,
  HOUSING_STATUS_LABELS,
} from "@visitproof/types";
import ProfileImageUpload from "../../../components/ProfileImageUpload";
import { getInitials } from "../../../utils/string";
import AddressSection from "../../../components/AddressSection";

interface Props {
  parent: Parent;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  dob?: string;
  relationshipToChild?: string;
  custodyStatus?: string;
  phoneNumber?: string;
  email?: string;
  saveError?: string;
}

// Helper to convert Firestore Timestamp to date string for input
const timestampToDateString = (timestamp: any): string => {
  if (!timestamp) return "";
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Collapsible section component for better organization
const CollapsibleSection: React.FC<{
  title: string;
  description?: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, description, isExpanded, onToggle, children }) => (
  <div className="overflow-hidden border rounded-lg border-zinc-200 dark:border-zinc-700">
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center justify-between w-full px-5 py-4 text-left bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800"
    >
      <div>
        <span className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</span>
        {description && (
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{description}</p>
        )}
      </div>
      {isExpanded ? (
        <ChevronUpIcon className="w-5 h-5 text-zinc-500" />
      ) : (
        <ChevronDownIcon className="w-5 h-5 text-zinc-500" />
      )}
    </button>
    {isExpanded && <div className="px-5 py-5 space-y-6">{children}</div>}
  </div>
);

const EditParentButton: React.FC<Props> = ({ parent }) => {
  const { updateParent } = useParent();
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    contact: true,
    address: false,
    demographics: false,
    health: false,
  });

  // Address state (separate from formData for easier management)
  const [address, setAddress] = useState<Address | undefined>(parent.address);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const [formData, setFormData] = useState({
    firstName: parent.firstName ?? "",
    lastName: parent.lastName ?? "",
    dob: timestampToDateString(parent.dob),
    relationshipToChild: parent.relationshipToChild ?? "mother",
    custodyStatus: parent.custodyStatus ?? "custodial",
    mentalHealthConcerns: parent.mentalHealthConcerns ?? false,
    isCurrentlyReceivingTherapy: parent.isCurrentlyReceivingTherapy ?? false,
    historyOfSubstanceAbuse: parent.historyOfSubstanceAbuse ?? false,
    notes: parent.notes ?? "",
    avatarUrl: parent.avatarUrl ?? "",
    // Contact
    phoneNumber: parent.phoneNumber ?? "",
    alternatePhone: parent.alternatePhone ?? "",
    email: parent.email ?? "",
    // Demographics
    sex: (parent.sex ?? "") as Sex | "",
    race: (parent.race ?? "") as Race | "",
    primaryLanguage: (parent.primaryLanguage ?? "") as PrimaryLanguage | "",
    primaryLanguageOther: parent.primaryLanguageOther ?? "",
    translatorNeeded: parent.translatorNeeded ?? false,
    maritalStatus: (parent.maritalStatus ?? "") as MaritalStatus | "",
    incomeLevel: (parent.incomeLevel ?? "") as IncomeLevel | "",
    educationLevel: (parent.educationLevel ?? "") as EducationLevel | "",
    employmentStatus: (parent.employmentStatus ?? "") as EmploymentStatus | "",
    housingStatus: (parent.housingStatus ?? "") as HousingStatus | "",
    isRuralArea: parent.isRuralArea ?? false,
    // Health
    medicalProblems: parent.medicalProblems ?? "",
    allergies: parent.allergies ?? "",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  // Reset form fields when `parent` changes or when the dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        firstName: parent.firstName ?? "",
        lastName: parent.lastName ?? "",
        dob: timestampToDateString(parent.dob),
        relationshipToChild: parent.relationshipToChild ?? "mother",
        custodyStatus: parent.custodyStatus ?? "custodial",
        mentalHealthConcerns: parent.mentalHealthConcerns ?? false,
        isCurrentlyReceivingTherapy: parent.isCurrentlyReceivingTherapy ?? false,
        historyOfSubstanceAbuse: parent.historyOfSubstanceAbuse ?? false,
        notes: parent.notes ?? "",
        avatarUrl: parent.avatarUrl ?? "",
        // Contact
        phoneNumber: parent.phoneNumber ?? "",
        alternatePhone: parent.alternatePhone ?? "",
        email: parent.email ?? "",
        // Demographics
        sex: (parent.sex ?? "") as Sex | "",
        race: (parent.race ?? "") as Race | "",
        primaryLanguage: (parent.primaryLanguage ?? "") as PrimaryLanguage | "",
        primaryLanguageOther: parent.primaryLanguageOther ?? "",
        translatorNeeded: parent.translatorNeeded ?? false,
        maritalStatus: (parent.maritalStatus ?? "") as MaritalStatus | "",
        incomeLevel: (parent.incomeLevel ?? "") as IncomeLevel | "",
        educationLevel: (parent.educationLevel ?? "") as EducationLevel | "",
        employmentStatus: (parent.employmentStatus ?? "") as EmploymentStatus | "",
        housingStatus: (parent.housingStatus ?? "") as HousingStatus | "",
        isRuralArea: parent.isRuralArea ?? false,
        // Health
        medicalProblems: parent.medicalProblems ?? "",
        allergies: parent.allergies ?? "",
      });
      // Reset section expansion
      setExpandedSections({
        contact: true,
        address: false,
        demographics: false,
        health: false,
      });
      // Reset address
      setAddress(parent.address);
    }
  }, [parent, isOpen]);

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required.";
    if (!formData.lastName.trim())
      newErrors.lastName = "Last name is required.";
    // DOB is optional for adults
    if (formData.dob && isNaN(Date.parse(formData.dob)))
      newErrors.dob = "Invalid date format.";
    if (!formData.relationshipToChild)
      newErrors.relationshipToChild = "Relationship to child is required.";
    if (!formData.custodyStatus)
      newErrors.custodyStatus = "Custody status is required.";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required.";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email address.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      let avatarUrl = formData.avatarUrl;

      if (file) {
        const path = account?.id
          ? storagePaths.parentAvatar(account.id, parent.id, file.name)
          : `parents/${parent.id}/${file.name}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, file);
        avatarUrl = await getDownloadURL(fileRef);
      }

      // Convert empty strings to undefined for optional fields
      const updatedData: Partial<Parent> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        // DOB is optional. Parse at noon UTC so the calendar day is stable in
        // every US timezone (plain `new Date("YYYY-MM-DD")` is UTC midnight,
        // which reads as the day before in the Americas).
        dob: formData.dob ? parseDateToTimestamp(formData.dob) : undefined,
        relationshipToChild: formData.relationshipToChild as RelationshipToChild,
        custodyStatus: formData.custodyStatus as CustodyStatus,
        mentalHealthConcerns: formData.mentalHealthConcerns,
        isCurrentlyReceivingTherapy: formData.isCurrentlyReceivingTherapy,
        historyOfSubstanceAbuse: formData.historyOfSubstanceAbuse,
        notes: formData.notes || undefined,
        avatarUrl: avatarUrl || undefined,
        // Contact
        phoneNumber: formData.phoneNumber,
        alternatePhone: formData.alternatePhone || undefined,
        email: formData.email || undefined,
        // Demographics
        sex: formData.sex || undefined,
        race: formData.race || undefined,
        primaryLanguage: formData.primaryLanguage || undefined,
        primaryLanguageOther: formData.primaryLanguage === "other" ? formData.primaryLanguageOther : undefined,
        translatorNeeded: formData.translatorNeeded,
        maritalStatus: formData.maritalStatus || undefined,
        incomeLevel: formData.incomeLevel || undefined,
        educationLevel: formData.educationLevel || undefined,
        employmentStatus: formData.employmentStatus || undefined,
        housingStatus: formData.housingStatus || undefined,
        isRuralArea: formData.isRuralArea,
        // Health
        medicalProblems: formData.medicalProblems || undefined,
        allergies: formData.allergies || undefined,
        // Address
        address: address || undefined,
      };

      await updateParent(parent.id, updatedData);
      clearForm();
    } catch (error) {
      setErrors({
        saveError: "Failed to update parent. Please try again later.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setIsOpen(false);
    setFile(null);
    setErrors({});
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={clearForm} size="2xl">
        <DialogTitle>Edit Parent</DialogTitle>
        <DialogBody className="space-y-8 max-h-[70vh] overflow-y-auto">
          {errors.saveError && (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <ErrorMessage>{errors.saveError}</ErrorMessage>
            </div>
          )}

          {/* Profile Image */}
          <div className="flex justify-center">
            <ProfileImageUpload
              currentImageUrl={formData.avatarUrl}
              initials={getInitials(`${formData.firstName} ${formData.lastName}`)}
              onFileSelected={setFile}
              size="lg"
              disabled={isSaving}
            />
          </div>

          {/* Personal Information */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Personal Information
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Basic details about this parent or guardian
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field>
                <Label>First Name</Label>
                <Input
                  type="text"
                  value={formData.firstName}
                  placeholder="Enter first name"
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                />
                {errors.firstName && (
                  <ErrorMessage>{errors.firstName}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  value={formData.lastName}
                  placeholder="Enter last name"
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                />
                {errors.lastName && (
                  <ErrorMessage>{errors.lastName}</ErrorMessage>
                )}
              </Field>
            </div>
            <Field>
              <Label>Date of Birth (Optional)</Label>
              <Input
                type="date"
                value={formData.dob}
                onChange={(e) => handleInputChange("dob", e.target.value)}
                className="sm:max-w-xs"
              />
              {errors.dob && <ErrorMessage>{errors.dob}</ErrorMessage>}
            </Field>
          </section>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Case Information */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Case Information
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Relationship and custody details
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field>
                <Label>Relationship to Child</Label>
                <Select
                  value={formData.relationshipToChild}
                  onChange={(e) =>
                    handleInputChange(
                      "relationshipToChild",
                      e.target.value as RelationshipToChild
                    )
                  }
                >
                  {Object.entries(RelationshipToChild).map(([key, value]) => (
                    <option key={key} value={value}>
                      {key}
                    </option>
                  ))}
                </Select>
                {errors.relationshipToChild && (
                  <ErrorMessage>{errors.relationshipToChild}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Custody Status</Label>
                <Select
                  value={formData.custodyStatus}
                  onChange={(e) =>
                    handleInputChange(
                      "custodyStatus",
                      e.target.value as CustodyStatus
                    )
                  }
                >
                  {Object.entries(CustodyStatus).map(([key, value]) => (
                    <option key={key} value={value}>
                      {key}
                    </option>
                  ))}
                </Select>
                {errors.custodyStatus && (
                  <ErrorMessage>{errors.custodyStatus}</ErrorMessage>
                )}
              </Field>
            </div>
          </section>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Health & History */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Health & History
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Relevant health and background information
              </p>
            </div>
            <div className="space-y-5">
              <SwitchField>
                <Label>Mental Health Concerns</Label>
                <Switch
                  checked={formData.mentalHealthConcerns}
                  onChange={() =>
                    handleInputChange("mentalHealthConcerns", !formData.mentalHealthConcerns)
                  }
                />
              </SwitchField>
              <SwitchField>
                <Label>Currently Receiving Therapy</Label>
                <Switch
                  checked={formData.isCurrentlyReceivingTherapy}
                  onChange={() =>
                    handleInputChange(
                      "isCurrentlyReceivingTherapy",
                      !formData.isCurrentlyReceivingTherapy
                    )
                  }
                />
              </SwitchField>
              <SwitchField>
                <Label>History of Substance Abuse</Label>
                <Switch
                  checked={formData.historyOfSubstanceAbuse}
                  onChange={() =>
                    handleInputChange("historyOfSubstanceAbuse", !formData.historyOfSubstanceAbuse)
                  }
                />
              </SwitchField>
            </div>
          </section>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Notes */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Additional Notes
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Any other relevant information about this parent
              </p>
            </div>
            <Field>
              <Textarea
                rows={4}
                value={formData.notes}
                placeholder="Enter any additional notes, concerns, or relevant information..."
                onChange={(e) => handleInputChange("notes", e.target.value)}
              />
            </Field>
          </section>

          {/* Collapsible Sections for Additional Info */}
          <div className="space-y-4">
            {/* Contact Information */}
            <CollapsibleSection
              title="Contact Information"
              description="Phone, email, and alternate contact"
              isExpanded={expandedSections.contact}
              onToggle={() => toggleSection("contact")}
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={formData.phoneNumber}
                    placeholder="(555) 555-5555"
                    onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  />
                  {errors.phoneNumber && (
                    <ErrorMessage>{errors.phoneNumber}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Alternate Phone</Label>
                  <Input
                    type="tel"
                    value={formData.alternatePhone}
                    placeholder="(555) 555-5555"
                    onChange={(e) => handleInputChange("alternatePhone", e.target.value)}
                  />
                </Field>
              </div>
              <Field>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  placeholder="email@example.com"
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </Field>
            </CollapsibleSection>

            {/* Address */}
            <CollapsibleSection
              title="Address"
              description="Home address with Google autocomplete"
              isExpanded={expandedSections.address}
              onToggle={() => toggleSection("address")}
            >
              <AddressSection
                address={address}
                onChange={setAddress}
                disabled={isSaving}
              />
            </CollapsibleSection>

            {/* Demographics */}
            <CollapsibleSection
              title="Demographics"
              description="For grant reporting purposes"
              isExpanded={expandedSections.demographics}
              onToggle={() => toggleSection("demographics")}
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field>
                  <Label>Sex</Label>
                  <Select
                    value={formData.sex}
                    onChange={(e) => handleInputChange("sex", e.target.value as Sex)}
                  >
                    <option value="">Select...</option>
                    {Object.entries(SEX_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Race/Ethnicity</Label>
                  <Select
                    value={formData.race}
                    onChange={(e) => handleInputChange("race", e.target.value as Race)}
                  >
                    <option value="">Select...</option>
                    {Object.entries(RACE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field>
                  <Label>Primary Language</Label>
                  <Select
                    value={formData.primaryLanguage}
                    onChange={(e) => handleInputChange("primaryLanguage", e.target.value as PrimaryLanguage)}
                  >
                    <option value="">Select...</option>
                    {Object.entries(LANGUAGE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                {formData.primaryLanguage === "other" && (
                  <Field>
                    <Label>Other Language</Label>
                    <Input
                      type="text"
                      value={formData.primaryLanguageOther}
                      placeholder="Specify language"
                      onChange={(e) => handleInputChange("primaryLanguageOther", e.target.value)}
                    />
                  </Field>
                )}
              </div>

              <div className="flex flex-wrap gap-8">
                <SwitchField>
                  <Label>Translator Needed</Label>
                  <Switch
                    checked={formData.translatorNeeded}
                    onChange={() => handleInputChange("translatorNeeded", !formData.translatorNeeded)}
                  />
                </SwitchField>
                <SwitchField>
                  <Label>Rural Area</Label>
                  <Switch
                    checked={formData.isRuralArea}
                    onChange={() => handleInputChange("isRuralArea", !formData.isRuralArea)}
                  />
                </SwitchField>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field>
                  <Label>Marital Status</Label>
                  <Select
                    value={formData.maritalStatus}
                    onChange={(e) => handleInputChange("maritalStatus", e.target.value as MaritalStatus)}
                  >
                    <option value="">Select...</option>
                    {Object.entries(MARITAL_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Employment Status</Label>
                  <Select
                    value={formData.employmentStatus}
                    onChange={(e) => handleInputChange("employmentStatus", e.target.value as EmploymentStatus)}
                  >
                    <option value="">Select...</option>
                    {Object.entries(EMPLOYMENT_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field>
                  <Label>Housing Status</Label>
                  <Select
                    value={formData.housingStatus}
                    onChange={(e) => handleInputChange("housingStatus", e.target.value as HousingStatus)}
                  >
                    <option value="">Select...</option>
                    {Object.entries(HOUSING_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Income Level</Label>
                  <Select
                    value={formData.incomeLevel}
                    onChange={(e) => handleInputChange("incomeLevel", e.target.value as IncomeLevel)}
                  >
                    <option value="">Select...</option>
                    {Object.entries(INCOME_LEVEL_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Education Level</Label>
                  <Select
                    value={formData.educationLevel}
                    onChange={(e) => handleInputChange("educationLevel", e.target.value as EducationLevel)}
                  >
                    <option value="">Select...</option>
                    {Object.entries(EDUCATION_LEVEL_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </CollapsibleSection>

            {/* Health Information */}
            <CollapsibleSection
              title="Health Information"
              description="Medical conditions and allergies"
              isExpanded={expandedSections.health}
              onToggle={() => toggleSection("health")}
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field>
                  <Label>Medical Conditions</Label>
                  <Input
                    type="text"
                    value={formData.medicalProblems}
                    placeholder="Any medical conditions"
                    onChange={(e) => handleInputChange("medicalProblems", e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Allergies</Label>
                  <Input
                    type="text"
                    value={formData.allergies}
                    placeholder="Known allergies"
                    onChange={(e) => handleInputChange("allergies", e.target.value)}
                  />
                </Field>
              </div>
            </CollapsibleSection>
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditParentButton;
